import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const State = () => {
  const { countryId } = useParams();
  const { hasPermission } = useAuth();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [metaFilter, setMetaFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchStates();
  }, [currentPage, searchQuery, metaFilter, countryId, pageLimit]);

  const fetchStates = async () => {
    setLoading(true);
    try {
      const response = await apiService.post('stateList1', {
        countryid: countryId,
        search: searchQuery,
        pending: metaFilter,
        start: (currentPage - 1) * pageLimit,
        length: pageLimit
      });
      
      if (response.data) {
        setStates(response.data);
        setTotalPages(Math.ceil(response.recordsFiltered / pageLimit));
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStateStatus = async (id) => {
    try {
      await apiService.get(`stateStatus/${id}`);
      setStates(prevStates => 
        prevStates.map(state => 
          state._id === id ? { ...state, status: state.status === 1 ? 0 : 1 } : state
        )
      );
    } catch (error) {
      console.error('Error updating state status:', error);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">«</button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 border rounded-lg ${currentPage === i ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-50'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pages.push(
        <button key="last" onClick={() => setCurrentPage(totalPages)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">»</button>
      );
    }

    return pages;
  };

  return (
    <div className="p-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <nav className="text-xs sm:text-sm flex flex-wrap items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Locations</span>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/country" className="text-gray-500 hover:text-gray-700">Country</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">States</span>
        </nav>
        {hasPermission('location_page', 'state', 'add') && (
          <Link to={`/add-state/${countryId}`} className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white text-sm rounded-lg hover:bg-[#009ef7] transition-colors">
            <i className="bi bi-plus-lg mr-2"></i>Add New
          </Link>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-full sm:min-w-[200px]">
            <div className="relative">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search query here.."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
          </div>
          {hasPermission('location_page', 'state', 'edit') && (
            <select
              value={metaFilter}
              onChange={(e) => { setMetaFilter(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-auto px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            >
              <option value="All">Show All</option>
              <option value="1">Content</option>
              <option value="0">No Meta Data</option>
            </select>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Image</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                {hasPermission('location_page', 'city', 'view') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">City</th>
                )}
                {hasPermission('location_page', 'state', 'edit') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Live</th>
                )}
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">View</th>
                {hasPermission('location_page', 'state', 'edit') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <i className="bi bi-arrow-repeat animate-spin text-2xl"></i>
                    <p className="mt-2">Loading...</p>
                  </td>
                </tr>
              ) : states.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No states found</td>
                </tr>
              ) : (
                states.map((state, index) => (
                  <tr key={state._id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-2 sm:px-4 py-3 text-sm text-gray-900">{(currentPage - 1) * pageLimit + index + 1}</td>
                    <td className="px-2 sm:px-4 py-3">
                      <img
                        src={state.image ? `${mediaUrl}${state.image}` : '/assets/media/svg/files/blank-image.svg'}
                        alt={state.name}
                        className="w-20 sm:w-32 h-12 sm:h-20 object-cover rounded"
                      />
                    </td>
                    <td className="px-2 sm:px-2 sm:px-4 py-3 text-sm text-gray-900 font-medium">{state.name}</td>
                    {hasPermission('location_page', 'city', 'view') && (
                      <td className="px-4 py-3">
                        <Link to={`/city/${state._id}`} className="inline-flex items-center justify-center w-8 h-8 bg-[#e8f4fd] hover:bg-[#e8f4fd] rounded-lg transition-colors">
                          <i className="bi bi-building text-[#009ef7]"></i>
                        </Link>
                      </td>
                    )}
                    {hasPermission('location_page', 'state', 'edit') && (
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={state.status === 1}
                            onChange={() => toggleStateStatus(state._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#009ef7] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <a
                        href={`https://travellinglark.com/location/${state.countryId?.slug}/${state.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 bg-[#e8f4fd] hover:bg-[#e8f4fd] rounded-lg transition-colors"
                      >
                        <i className="bi bi-eye text-[#009ef7]"></i>
                      </a>
                    </td>
                    {hasPermission('location_page', 'state', 'edit') && (
                      <td className="px-4 py-3">
                        <Link to={`/edit-state/${state._id}`} className="inline-flex items-center justify-center w-8 h-8 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
                          <i className="bi bi-pencil text-green-600"></i>
                        </Link>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && states.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs sm:text-sm text-gray-700">Show</span>
              <select
                value={pageLimit}
                onChange={(e) => { setPageLimit(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
              <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({(currentPage - 1) * pageLimit + 1} to {Math.min(currentPage * pageLimit, (currentPage - 1) * pageLimit + states.length)} of {totalPages * pageLimit})</span>
            </div>
            <div className="flex flex-wrap gap-1 justify-center">
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default State;
